import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

/**
 * Questionnaire save status. Drives the header save-status indicator and
 * powers optimistic, debounced autosave of catalog answers.
 *
 * - `idle`     nothing to save (initial / freshly loaded)
 * - `unsaved`  the user changed an answer; a debounced autosave is queued
 * - `saving`   a PUT is in flight
 * - `saved`    the last save succeeded
 * - `error`    the last save failed; the user can retry
 */
export type QuestionnaireSaveStatus =
  | "idle"
  | "unsaved"
  | "saving"
  | "saved"
  | "error";

/** Default debounce window for autosave (ms). */
const AUTOSAVE_DEBOUNCE_MS = 1500;

interface QuestionnaireSaveContextValue {
  status: QuestionnaireSaveStatus;
  lastSavedAt: number | null;
  errorMessage: string | null;
  /** Run a save now (manual save button, or flushing a pending autosave). */
  runSave: (performPut: () => Promise<unknown>) => Promise<boolean>;
  /** Queue a debounced autosave; replaces any pending one. */
  scheduleSave: (performPut: () => Promise<unknown>, delayMs?: number) => void;
  /** Retry the most recent save payload after a failure. */
  retry: () => Promise<boolean>;
  /** Flag local edits that are not on the server yet (manual-save mode). */
  markUnsaved: () => void;
}

const QuestionnaireSaveContext = createContext<
  QuestionnaireSaveContextValue | undefined
>(undefined);

const errorToMessage = (err: unknown): string => {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === "string") {
    return err;
  }
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
};

export const QuestionnaireSaveProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [status, setStatus] = useState<QuestionnaireSaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<boolean>(false);
  // Latest payload to send once the in-flight save settles (trailing coalesce).
  const pendingRef = useRef<(() => Promise<unknown>) | null>(null);
  // Most recent payload, used by retry().
  const lastPerformRef = useRef<(() => Promise<unknown>) | null>(null);

  const runSave = useCallback(
    async (performPut: () => Promise<unknown>): Promise<boolean> => {
      lastPerformRef.current = performPut;
      // Cancel any debounce: this save supersedes the queued one.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // Never block input: if a save is already running, remember the latest
      // payload and let the in-flight save flush it when it settles.
      if (inFlightRef.current) {
        pendingRef.current = performPut;
        return false;
      }
      inFlightRef.current = true;
      setStatus("saving");
      setErrorMessage(null);
      let ok = false;
      try {
        await performPut();
        ok = true;
        setStatus("saved");
        setLastSavedAt(Date.now());
      } catch (err) {
        setStatus("error");
        setErrorMessage(errorToMessage(err));
      } finally {
        inFlightRef.current = false;
        const next = pendingRef.current;
        pendingRef.current = null;
        if (next) {
          // A newer edit arrived mid-flight: persist it too.
          void runSave(next);
        }
      }
      return ok;
    },
    [],
  );

  const scheduleSave = useCallback(
    (
      performPut: () => Promise<unknown>,
      delayMs: number = AUTOSAVE_DEBOUNCE_MS,
    ): void => {
      lastPerformRef.current = performPut;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      // Reflect that there are edits not yet persisted (unless mid-save).
      setStatus((prev) => (prev === "saving" ? prev : "unsaved"));
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void runSave(performPut);
      }, delayMs);
    },
    [runSave],
  );

  const retry = useCallback((): Promise<boolean> => {
    if (lastPerformRef.current) {
      return runSave(lastPerformRef.current);
    }
    return Promise.resolve(false);
  }, [runSave]);

  const markUnsaved = useCallback((): void => {
    setStatus((prev) => (prev === "saving" ? prev : "unsaved"));
  }, []);

  return (
    <QuestionnaireSaveContext.Provider
      value={{
        status,
        lastSavedAt,
        errorMessage,
        runSave,
        scheduleSave,
        retry,
        markUnsaved,
      }}
    >
      {children}
    </QuestionnaireSaveContext.Provider>
  );
};

// Fallback used when the QuestionnaireHeader is reused outside a provider
// (e.g. an alternate shell). Manual saves still run; status is just not tracked
// and autosave is disabled there.
const FALLBACK_SAVE: QuestionnaireSaveContextValue = {
  status: "idle",
  lastSavedAt: null,
  errorMessage: null,
  runSave: async (performPut) => {
    try {
      await performPut();
      return true;
    } catch {
      return false;
    }
  },
  scheduleSave: () => {},
  retry: () => Promise.resolve(false),
  markUnsaved: () => {},
};

export const useQuestionnaireSave = (): QuestionnaireSaveContextValue => {
  return useContext(QuestionnaireSaveContext) ?? FALLBACK_SAVE;
};

export { QuestionnaireSaveIndicator } from "./indicator";
